import { z } from "zod";

import { centsToDecimalString, parseDecimalInput, toCents } from "./decimal";

/**
 * Shared zod fragments for the forms standard (§3.1/§3.6). Every message is an
 * i18n key under `forms.validation.*` — `FormMessage` resolves it through
 * `t()`, so schemas stay locale-agnostic.
 *
 * All numeric/money fragments are string-in (what the input holds) with a
 * typed output. `z.coerce.number()` is banned in form schemas: it turns `""`
 * into `0` and rejects `"12,5"` — use these fragments instead.
 */

/** Non-empty trimmed string (whitespace-only fails). */
export const requiredString = (message = "forms.validation.required") =>
	z.string().trim().min(1, message);

/**
 * Required email address — `""` yields the generic required message, malformed
 * text the `invalidEmail` message. Client-side format check is UX only; the
 * backend stays the source of truth for invitations/accounts.
 */
export const email = () =>
	z
		.string()
		.trim()
		.min(1, "forms.validation.required")
		.pipe(z.email("forms.validation.invalidEmail"));

/** Trimmed string where `""` becomes `undefined` (for optional API fields). */
export const optionalTrimmed = () =>
	z
		.string()
		.trim()
		.transform((value) => (value === "" ? undefined : value));

/** Required decimal input (comma or dot) parsed to a number. */
export const requiredNumber = () =>
	z.string().transform((value, ctx) => {
		const parsed = parseDecimalInput(value);
		if (parsed === undefined) {
			ctx.addIssue({
				code: "custom",
				message:
					value.trim() === ""
						? "forms.validation.required"
						: "forms.validation.invalidNumber",
			});
			return z.NEVER;
		}
		return parsed;
	});

/** Native date input value (`YYYY-MM-DD`, from `formatForDateInput`). */
export const dateInput = () =>
	z
		.string()
		.min(1, "forms.validation.required")
		.regex(/^\d{4}-\d{2}-\d{2}$/, "forms.validation.invalidDate");

/**
 * Money input (comma or dot, cents precision) canonicalized to the API's
 * decimal-string wire format ("1234.50"). Output is a string, never a float —
 * client-side sums/compares must go through `toCents` (§3.6).
 */
export const money = ({ positive = true }: { positive?: boolean } = {}) =>
	z.string().transform((value, ctx) => {
		const cents = toCents(value);
		if (cents === undefined) {
			ctx.addIssue({
				code: "custom",
				message:
					value.trim() === ""
						? "forms.validation.required"
						: "forms.validation.invalidNumber",
			});
			return z.NEVER;
		}
		if (positive && cents <= 0) {
			ctx.addIssue({
				code: "custom",
				message: "forms.validation.mustBePositive",
			});
			return z.NEVER;
		}
		return centsToDecimalString(cents);
	});

/**
 * Optional money as a **validate-only** string (empty allowed, invalid text
 * errors) — the value stays `string` so it composes inside a mixed schema
 * object without the RHF input/output generics diverging (a `string →
 * string | undefined` transform next to required `string` fields breaks them).
 * Canonicalize to the wire string in the payload builder via {@link toMoneyStringOrNull}.
 */
export const optionalMoneyText = () =>
	z
		.string()
		.refine(
			(value) => value.trim() === "" || toCents(value) !== undefined,
			"forms.validation.invalidNumber",
		);

/**
 * Canonicalizes an optional money input to the API decimal string, or `null`
 * when empty/invalid (pairs with {@link optionalMoneyText}, which has already
 * rejected invalid non-empty text at validation time).
 */
export function toMoneyStringOrNull(value: string): string | null {
	const cents = toCents(value);
	return cents === undefined ? null : centsToDecimalString(cents);
}
